/// The quarter goal card's copy (funnel dashboard). The backend decides every
/// number and status (functions/src/quarterGoalCore.ts); this file only picks
/// the words for them, verbatim from the design (2026-11-02). Types mirror the
/// backend's QuarterGoalLive, duplicated across the functions/web boundary by
/// convention. Dependency-free so vitest can check the strings from the root.

export type Pace = 'waiting' | 'on' | 'ahead' | 'behind' | 'reached' | 'met' | 'missed'
export type GuardrailStatus = 'early' | 'holding' | 'broken' | 'unavailable'

export interface QuarterGoalLive {
  source: 'live'
  asOf: string
  today: string
  quarter: { start: string; end: string; days: number; dayOfQuarter: number; phase: 'running' | 'closing' | 'final' }
  proceeds: {
    targetUSD: number
    sumUSD: number
    sales: number
    reportedThrough: string | null
    reportedDays: number
    missingDays: string[]
    expectedUSD: number
    gapUSD: number
    bandUSD: number
    pace: Pace
    neededPerDayUSD: number | null
    reachedOn: string | null
    unconvertedCurrencies: string[]
    series: { date: string; cumUSD: number; cumSales: number }[]
    notYetReported: { sales: number; afterDate: string | null }
  }
  drivers: {
    purchases30d: { value: number; aim: number; weekAgo: number; windowStart: string; windowEnd: string; reached: boolean } | null
    searchDownloads30d: {
      value: number
      aim: number
      weekAgo: number | null
      windowStart: string
      windowEnd: string
      missingDays: number
      reached: boolean
    } | null
    reasons?: { purchases30d?: string; searchDownloads30d?: string }
  }
  watch: {
    firstDownloads30d: { value: number; searchSharePct: number; windowEnd: string } | null
    boughtByDay7: {
      month: string
      cohort: number
      bought: number
      pct: number
      complete: boolean
      baselinePct: number
      baselineLabel: string
    } | null
  }
  guardrails: {
    d1: {
      floorPct: number
      minCohort: number
      status: GuardrailStatus
      reason?: string
      months: { month: string; cohort: number; retained: number; pct: number; complete: boolean }[]
    }
    ratings: { floorAvg: number; minCount: number; status: GuardrailStatus; reason?: string; count: number; avg: number | null }
    refunds: {
      ceilingPct: number
      minPurchases: number
      status: GuardrailStatus
      reason?: string
      purchases: number
      refunded: number
      pct: number | null
    }
    broken: ('d1' | 'ratings' | 'refunds')[]
  }
  note: string
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/// "2026-11-01" reads "Nov 1".
export function dayLabel(ymd: string): string {
  const [, m, d] = ymd.split('-').map(Number)
  return `${MONTHS[(m ?? 1) - 1]} ${d}`
}
/// "2026-11" reads "November".
export function monthLong(ym: string): string {
  return MONTHS_LONG[Number(ym.split('-')[1]) - 1] ?? ym
}
/// "2026-11" reads "Nov".
export function monthShort(ym: string): string {
  return MONTHS[Number(ym.split('-')[1]) - 1] ?? ym
}

const num = (n: number) => n.toLocaleString('en-US')
/// Money in whole dollars (the card's rule), sign left to the words.
export const usd0 = (n: number) => '$' + Math.round(Math.abs(n)).toLocaleString('en-US')
/// Money with cents: "needed per day" only.
export const usd2 = (n: number) => '$' + Math.abs(n).toFixed(2)
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

export function metaLine(q: QuarterGoalLive): string {
  const span = `${dayLabel(q.quarter.start)} to ${dayLabel(q.quarter.end)}`
  return q.quarter.phase === 'running'
    ? `${span} · day ${q.quarter.dayOfQuarter} of ${q.quarter.days}`
    : `${span} · ended ${dayLabel(q.quarter.end)}`
}

export function salesText(n: number): string {
  return `${num(n)} ${plural(n, 'sale', 'sales')}`
}

export type Tone = 'emerald' | 'amber' | 'rose'
export interface Verdict {
  word: string
  tone: Tone
  line1: string
  line2: string | null
}

/// The pace verdict and its detail lines (design section 3).
export function verdict(q: QuarterGoalLive): Verdict {
  const p = q.proceeds
  const through = p.reportedThrough ? dayLabel(p.reportedThrough) : ''
  const end = dayLabel(q.quarter.end)
  const need = p.neededPerDayUSD != null ? `Needs ${usd2(p.neededPerDayUSD)} a day from here.` : null
  const gapLine = () => {
    if (Math.abs(p.gapUSD) < 0.5) return `Right on the ${usd0(p.expectedUSD)} expected by ${through}.`
    return p.gapUSD >= 0
      ? `${usd0(p.gapUSD)} ahead of the ${usd0(p.expectedUSD)} expected by ${through}.`
      : `${usd0(p.gapUSD)} short of the ${usd0(p.expectedUSD)} expected by ${through}.`
  }
  if (q.quarter.phase === 'closing' && p.pace !== 'reached' && p.pace !== 'waiting') {
    return {
      word: 'Closing',
      tone: 'amber',
      line1: `The quarter ended ${end}. Apple has reported through ${through}; the final sum lands in a day or two.`,
      line2: null,
    }
  }
  switch (p.pace) {
    case 'waiting': {
      const n = p.notYetReported.sales
      const start = dayLabel(q.quarter.start)
      return {
        word: 'Waiting for Apple',
        tone: 'amber',
        line1: `Apple reports each day 1 to 2 days late. ${start} has not arrived yet.`,
        line2: n > 0 ? `${salesText(n)} since ${start} ${plural(n, 'is', 'are')} not counted yet.` : null,
      }
    }
    case 'on':
      return { word: 'On pace', tone: 'emerald', line1: gapLine(), line2: need }
    case 'ahead':
      return { word: 'Ahead of pace', tone: 'emerald', line1: gapLine(), line2: need }
    case 'behind':
      return { word: 'Behind pace', tone: 'amber', line1: gapLine(), line2: need }
    case 'reached':
      return {
        word: 'Target reached',
        tone: 'emerald',
        line1: p.reachedOn ? `Reached ${dayLabel(p.reachedOn)}. The quarter runs to ${end}.` : `The quarter runs to ${end}.`,
        line2: null,
      }
    case 'met':
      return { word: 'Goal met', tone: 'emerald', line1: `${usd0(p.sumUSD - p.targetUSD)} over the target.`, line2: null }
    case 'missed':
      return { word: 'Goal missed', tone: 'rose', line1: `${usd0(p.targetUSD - p.sumUSD)} short of the target.`, line2: null }
  }
}

/// A ratings average at one decimal, or two when one would read at or above
/// the floor it is under (4.29 would otherwise print as 4.3 beside Broken).
export function avgText(avg: number, floor: number): string {
  const one = avg.toFixed(1)
  return avg < floor && Number(one) >= floor ? avg.toFixed(2) : one
}

/// The month a D1 reading is broken on: the first one with enough installs
/// under the floor (the backend's rule for the status).
function brokenD1Month(q: QuarterGoalLive) {
  const g = q.guardrails.d1
  return g.months.find((m) => m.cohort >= g.minCohort && m.pct < g.floorPct) ?? null
}

/// The rose banner under the verdict: one sentence per broken guardrail.
export function brokenLines(q: QuarterGoalLive): string[] {
  const g = q.guardrails
  const out: string[] = []
  for (const k of g.broken) {
    if (k === 'd1') {
      const m = brokenD1Month(q)
      if (m) out.push(`Guardrail broken: D1 for ${monthLong(m.month)} installs is ${m.pct}%, under the ${g.d1.floorPct}% floor.`)
    } else if (k === 'ratings' && g.ratings.avg != null) {
      out.push(
        `Guardrail broken: ratings this quarter average ${avgText(g.ratings.avg, g.ratings.floorAvg)} stars from ${num(g.ratings.count)}, under ${g.ratings.floorAvg}.`,
      )
    } else if (k === 'refunds') {
      out.push(
        `Guardrail broken: ${num(g.refunds.refunded)} of ${num(g.refunds.purchases)} purchases this quarter were refunded (${g.refunds.pct}%), over ${g.refunds.ceilingPct}%.`,
      )
    }
  }
  return out
}

/// The days Apple skipped, in one sentence.
export function missingDaysLine(days: string[]): string | null {
  if (days.length === 0) return null
  const list = days.map(dayLabel).join(' and ')
  return days.length === 1
    ? `Apple's report for ${list} is missing; that day is left out of the sum and the pace.`
    : `Apple's reports for ${list} are missing; those days are left out of the sum and the pace.`
}

/// Under the chart: the lag and the sales not counted yet while the quarter
/// runs, the count after the last reported day while it closes, nothing once
/// final or while waiting (the verdict carries it), plus any missing day.
export function lagLines(q: QuarterGoalLive): string[] {
  const p = q.proceeds
  const lines: string[] = []
  const n = p.notYetReported.sales
  if (p.reportedThrough && p.pace !== 'waiting') {
    const through = dayLabel(p.reportedThrough)
    if (q.quarter.phase === 'running') {
      lines.push(`Apple has reported through ${through} and runs 1 to 2 days behind.`)
      if (n > 0) lines.push(n === 1 ? '1 sale since then is not counted yet.' : `${num(n)} sales since then are not counted yet.`)
    } else if (q.quarter.phase === 'closing' && n > 0) {
      lines.push(`${salesText(n)} after ${through} ${plural(n, 'is', 'are')} not counted yet.`)
    }
  }
  const missing = missingDaysLine(p.missingDays)
  if (missing) lines.push(missing)
  return lines
}

/// "Up 2 from 43 a week ago", or null without a week-ago figure.
export function direction(value: number, weekAgo: number | null): string | null {
  if (weekAgo == null) return null
  if (value === weekAgo) return 'Same as a week ago'
  return value > weekAgo
    ? `Up ${num(value - weekAgo)} from ${num(weekAgo)} a week ago`
    : `Down ${num(weekAgo - value)} from ${num(weekAgo)} a week ago`
}

export interface DriverTileCopy {
  label: string
  value: string
  aim: string | null
  sub: string
  fillPct: number | null
}

export function purchasesTile(q: QuarterGoalLive): DriverTileCopy {
  const label = 'Paid purchases, 30 days'
  const d = q.drivers.purchases30d
  if (!d) return { label, value: 'Unavailable', aim: null, sub: q.drivers.reasons?.purchases30d ?? 'No reading this load.', fillPct: null }
  return {
    label,
    value: num(d.value),
    aim: `aim ${num(d.aim)}${d.reached ? ', reached' : ''}`,
    sub: [direction(d.value, d.weekAgo), 'real time'].filter(Boolean).join(' · '),
    fillPct: Math.min(100, (100 * d.value) / d.aim),
  }
}

export function searchTile(q: QuarterGoalLive): DriverTileCopy {
  const label = 'App Store search downloads, 30 days'
  const d = q.drivers.searchDownloads30d
  if (!d) return { label, value: 'Unavailable', aim: null, sub: q.drivers.reasons?.searchDownloads30d ?? 'No reading this load.', fillPct: null }
  const missing = d.missingDays > 0 ? ` (${d.missingDays} ${plural(d.missingDays, 'day', 'days')} missing)` : ''
  return {
    label,
    value: num(d.value),
    aim: `aim ${num(d.aim)}${d.reached ? ', reached' : ''}`,
    sub: [direction(d.value, d.weekAgo), `30 days to ${dayLabel(d.windowEnd)}${missing}`].filter(Boolean).join(' · '),
    fillPct: Math.min(100, (100 * d.value) / d.aim),
  }
}

const NO_READING = 'No reading'

/// The D1 guardrail row's name and reading.
export function d1Row(q: QuarterGoalLive): { name: string; reading: string } {
  const g = q.guardrails.d1
  const ms = g.months
  if (ms.length > 1) {
    const open = ms.find((m) => !m.complete)
    return {
      name: 'D1 by install month',
      reading:
        ms.map((m) => `${monthShort(m.month)} ${m.pct}%${m.complete ? '' : ' so far'}`).join(' · ') +
        (open ? ` (${num(open.cohort)} installs)` : ''),
    }
  }
  const m = ms[0]
  if (!m) {
    // Nothing matured yet: name the month the quarter is in.
    const month = (q.today >= q.quarter.start && q.today <= q.quarter.end ? q.today : q.quarter.start).slice(0, 7)
    return { name: `D1, ${monthLong(month)} installs`, reading: g.status === 'unavailable' ? NO_READING : 'None yet' }
  }
  return {
    name: `D1, ${monthLong(m.month)} installs`,
    reading: `${m.pct}% (${num(m.retained)} of ${num(m.cohort)}${m.complete ? '' : ' so far'})`,
  }
}

export function ratingsReading(q: QuarterGoalLive): string {
  const r = q.guardrails.ratings
  if (r.status === 'unavailable') return NO_READING
  if (r.count === 0 || r.avg == null) return 'None yet'
  return `${avgText(r.avg, r.floorAvg)} stars from ${num(r.count)} ${plural(r.count, 'rating', 'ratings')}`
}

export function refundsReading(q: QuarterGoalLive): string {
  const f = q.guardrails.refunds
  if (f.status === 'unavailable') return NO_READING
  return `${num(f.refunded)} of ${num(f.purchases)} purchases${f.purchases > 0 && f.pct != null ? ` (${f.pct}%)` : ''}`
}

export const STATUS_WORD: Record<GuardrailStatus, string> = {
  holding: 'Holding',
  broken: 'Broken',
  early: 'Too early',
  unavailable: 'Unavailable',
}

export const GUARDRAIL_NOTE = 'A guardrail reads once it has 200 installs, 5 ratings or 20 purchases behind it.'

/// The watched numbers, or null when neither came back.
export function watchLine(q: QuarterGoalLive): string | null {
  const f = q.watch.firstDownloads30d
  const b = q.watch.boughtByDay7
  const parts: string[] = []
  if (f) parts.push(`${num(f.value)} first-time downloads in 30 days (${Math.round(f.searchSharePct)}% from search).`)
  if (b) {
    parts.push(
      `Bought by day 7: ${b.pct}% of ${monthLong(b.month)} installs${b.complete ? '' : ' so far'}, against ${b.baselinePct}% for ${b.baselineLabel}.`,
    )
  }
  return parts.length ? `Watching, no target: ${parts.join(' ')}` : null
}

export const CALLS_LINE =
  "Craig's calls, never tuned by agents: price, free plays a day, paid ads, ads in the game, and all future content for full-game buyers."

const CARD_NOTE =
  'Net proceeds are what Apple pays out, from its daily sales reports, converted to USD at fixed approximate rates, ' +
  "so the bank deposit will differ by a few percent. The goal is judged on the quarter's sum. On pace means within " +
  "the normal swing of a few sales either way. Search downloads are Apple's first-time downloads from App Store " +
  'search, and may include the small number from Search Ads.'

const sentence = (s: string) => (/[.!?]$/.test(s.trim()) ? s.trim() : `${s.trim()}.`)

/// The card note, with the unconverted currencies and any unavailable
/// guardrail's reason appended.
export function cardNote(q: QuarterGoalLive): string {
  const extra: string[] = []
  const codes = q.proceeds.unconvertedCurrencies
  if (codes.length) extra.push(`Left out for lack of a rate: ${codes.join(', ')}.`)
  const g = q.guardrails
  const names: [string, { status: GuardrailStatus; reason?: string }][] = [
    ['D1', g.d1],
    ['Ratings', g.ratings],
    ['Refunds', g.refunds],
  ]
  for (const [name, x] of names) {
    if (x.status === 'unavailable') extra.push(`${name} unavailable: ${sentence(x.reason ?? 'the source did not answer')}`)
  }
  return [CARD_NOTE, ...extra].join(' ')
}

/// The chart's accessible name.
export function chartLabel(q: QuarterGoalLive): string {
  const p = q.proceeds
  return p.reportedThrough
    ? `Reported net proceeds ${usd0(p.sumUSD)} through ${dayLabel(p.reportedThrough)}, against ${usd0(p.expectedUSD)} at even pace.`
    : 'No day of the quarter has been reported yet.'
}
